/**
 * Error Handling Constants
 * ============================================================================
 *
 * Purpose:
 * --------
 * Centralized definition of error codes, severities, and categories for
 * the error handling system.
 *
 * This file defines:
 * - Error severity levels
 * - Error categories for classification
 * - Recovery strategies
 * - Error reporting configuration
 *
 * Design Principles:
 * ------------------
 * - Error codes are treated as PUBLIC CONTRACTS
 * - Values should be stable and backward-compatible
 * - All user-facing messages use i18n translation keys
 * - Error metadata supports debugging and monitoring
 *
 * Non-goals:
 * ----------
 * - Error transformation logic (handled by service)
 * - UI rendering (handled by components)
 * - Error reporting (handled by service)
 *
 * Any changes here affect error handling behavior across the entire
 * application and should be reviewed carefully.
 * ============================================================================
 */

/**
 * Error severity levels
 *
 * Used to classify errors by impact and determine handling strategy.
 */
export const ERROR_SEVERITY = {
  /**
   * Critical errors that break core functionality
   * Examples: Auth failure, payment processing failure
   * Action: Show error UI, log to monitoring, may block user
   */
  CRITICAL: 'critical',

  /**
   * High-priority errors that impact major features
   * Examples: Failed to load data, submission failed
   * Action: Show error message, log to monitoring, allow retry
   */
  HIGH: 'high',

  /**
   * Medium-priority errors that impact minor features
   * Examples: Failed to load optional data, non-critical API failure
   * Action: Show toast notification, log locally
   */
  MEDIUM: 'medium',

  /**
   * Low-priority errors that don't significantly impact UX
   * Examples: Analytics failure, optional service unavailable
   * Action: Silent fail, log locally only
   */
  LOW: 'low',

  /**
   * Informational errors for debugging
   * Examples: Deprecation warnings, validation hints
   * Action: Console log only (dev mode)
   */
  INFO: 'info',
} as const;

/**
 * Error categories for classification
 *
 * Groups errors by origin/type for better handling and reporting.
 */
export const ERROR_CATEGORY = {
  /**
   * Network-related errors (API calls, connectivity)
   */
  NETWORK: 'network',

  /**
   * Authentication/authorization errors
   */
  AUTH: 'auth',

  /**
   * Data validation errors
   */
  VALIDATION: 'validation',

  /**
   * Business logic errors
   */
  BUSINESS: 'business',

  /**
   * React component errors (caught by ErrorBoundary)
   */
  COMPONENT: 'component',

  /**
   * Third-party service errors (S3, payment gateway, etc.)
   */
  EXTERNAL: 'external',

  /**
   * Client-side runtime errors (JavaScript errors)
   */
  RUNTIME: 'runtime',

  /**
   * Unknown/uncategorized errors
   */
  UNKNOWN: 'unknown',
} as const;

/**
 * Error recovery strategies
 *
 * Defines how the system should attempt to recover from errors.
 */
export const RECOVERY_STRATEGY = {
  /**
   * No recovery possible - show error and let user decide
   */
  NONE: 'none',

  /**
   * Retry the failed operation
   */
  RETRY: 'retry',

  /**
   * Redirect to a safe page (login, home, etc.)
   */
  REDIRECT: 'redirect',

  /**
   * Reload the page/component
   */
  RELOAD: 'reload',

  /**
   * Use cached/fallback data
   */
  FALLBACK: 'fallback',

  /**
   * Custom recovery logic
   */
  CUSTOM: 'custom',
} as const;

/**
 * Error reporting configuration
 */
export const ERROR_REPORTING = {
  /**
   * Enable error reporting to external service (Sentry, etc.)
   * Set via environment variable
   */
  ENABLED: import.meta.env.VITE_ERROR_REPORTING_ENABLED === 'true',

  /**
   * Sample rate (0-1) for error reporting
   * 1.0 = report all errors, 0.5 = report 50% of errors
   */
  SAMPLE_RATE: parseFloat((import.meta.env.VITE_ERROR_SAMPLE_RATE as string | undefined) ?? '1.0'),

  /**
   * Environments where error reporting is active
   */
  ACTIVE_ENVIRONMENTS: ['production', 'staging'],

  /**
   * Current environment
   */
  CURRENT_ENVIRONMENT: import.meta.env.MODE,
} as const;

/**
 * Storage keys for error handling
 */
export const ERROR_STORAGE_KEYS = {
  /**
   * Local error log (for offline error tracking)
   */
  ERROR_LOG: 'bimanova_error_log',

  /**
   * Last error timestamp (to prevent error spam)
   */
  LAST_ERROR_TIME: 'bimanova_last_error_time',

  /**
   * Error reporting consent
   */
  REPORTING_CONSENT: 'bimanova_error_reporting_consent',
} as const;

/**
 * Error handling configuration
 */
export const ERROR_CONFIG = {
  /**
   * Maximum number of errors to store locally
   */
  MAX_LOCAL_ERRORS: 50,

  /**
   * Minimum time (ms) between showing error notifications
   * Prevents error spam to user
   */
  ERROR_THROTTLE_MS: 1000,

  /**
   * Maximum number of retry attempts for recoverable errors
   */
  MAX_RETRY_ATTEMPTS: 3,

  /**
   * Retry delay (ms) - increases exponentially
   */
  RETRY_DELAY_MS: 1000,

  /**
   * Show error details in development
   */
  SHOW_ERROR_DETAILS: import.meta.env.DEV,
} as const;

/**
 * Translation key prefixes
 *
 * All error messages should use these prefixes for i18n.
 */
export const ERROR_I18N_KEYS = {
  /**
   * Generic error messages
   * Example: 'errors:generic.unknown'
   */
  GENERIC: 'errors:generic',

  /**
   * Network error messages
   * Example: 'errors:network.timeout'
   */
  NETWORK: 'errors:network',

  /**
   * Auth error messages
   * Example: 'errors:auth.unauthorized'
   */
  AUTH: 'errors:auth',

  /**
   * Validation error messages
   * Example: 'errors:validation.required'
   */
  VALIDATION: 'errors:validation',

  /**
   * Business logic error messages
   * Example: 'errors:business.insufficientBalance'
   */
  BUSINESS: 'errors:business',

  /**
   * Component error messages
   * Example: 'errors:component.renderFailed'
   */
  COMPONENT: 'errors:component',

  /**
   * Recovery action messages
   * Example: 'errors:actions.retry'
   */
  ACTIONS: 'errors:actions',
} as const;
